import { BCMSRecord, DisabledPrompt, Link, ModalOpen, useBCMSModal, useGlobalController, useRoute } from "@bcm-dev/bcms-vite";
import ButtonContainer from "compid/shared/containers/ButtonContainer";
import { ActiveBadge, PromoInfoView } from "compid/shared/views/PromoView/components/views/PromoInfoView";
import WidgetView, { WidgetMainContentView } from "compid/shared/views/WidgetView";
import UIButton from "compid/ui/UIButton";
import { Bill } from "model";
import React from "react";
import { SVGAsset } from "res";
import "./styles.css";

/**
 * @function BillerDetailsUpsellView
 * @return {React.Component}
 * @param {OBJECT} icon - The icon for the upsell widget
 * @param {STRING} title - The title for the upsell widget
 * @param {STRING} description - The upsell widget description
 * @param {STRING} buttonText - The text used in the button for the upsell widget
 * @param {FUNCTION} buttonClick - The function to run on click of button
 * @exported true
 * @description This is the main component for the BillerDetailsUpsellView.
 */
export default function BillerDetailsUpsellView(props) {
  const icon = props.icon ?? <SVGAsset id="LightBulbIcon" className="light-bulb-icon" />;
  const title = props.title ?? "[title]";
  const description = props.description ?? "[description]";
  const className = props.className ?? "";
  const showeBillList = props.showeBillList ?? false;

  const buttonText = props.buttonText ?? "[buttonText]";
  const buttonClick = typeof props.buttonClick == "function" ? props.buttonClick : ()=>{};

  const showUpsellNotice = props.showNotice ?? true;
  const showActiveUpsellNotice = props.showActiveUpsellNotice ?? false;
  const showActiveBadge = props.showActiveBadge ?? false;

  const bills = props.eBillListBills ?? [];
  const billItems = bills.map(bill => <EBillListItem key={ `eBillItem_${ bill.id }` } bill={ bill } />, []);

  return(
    <div className={`${ className } biller-details-upsell-view`}>
      <WidgetView>
        <div className="flex-container flex-vertical flex-width">
          <WidgetMainContentView>
            <PromoInfoView dataPoint={ props.dataPoint } icon={icon} title={title} description={description}
            className="flex-width" activeBadge={ showActiveBadge && <ActiveBadge dataPoint={ props.dataPoint } />} />

            <ButtonContainer>
              <UIButton className="secondary-button" onClick={buttonClick} dataPoint={ props.dataPoint }>
                <span>{buttonText}</span>
              </UIButton>
            </ButtonContainer>
          </WidgetMainContentView>

          { showeBillList &&
            <div className="ebill-list-container">
              { billItems }
            </div>
          }

          { showUpsellNotice &&
            <div className="flex-container align-center upsell-notice">
              <SVGAsset id="LightBulbIcon" className="left-icon" />
              <DisabledPrompt>
                <Link>
                  <b>Remove the unwanted clutter and go paperless!</b>
                  <SVGAsset id="RightChevronIcon" className="align-center more-chevron" />
                </Link>
              </DisabledPrompt>
            </div> }

            {/* Only shows when eBills are active */}
            { showActiveUpsellNotice &&
            <div className="flex-container align-center upsell-notice">
              <SVGAsset id="LightBulbIcon" className="left-icon" />
              <DisabledPrompt>
                <Link>
                  <b>Looking to manage your eBills?</b>
                  <SVGAsset id="RightChevronIcon" className="align-center more-chevron" />
                </Link>
              </DisabledPrompt>
            </div> }
        </div>
      </WidgetView>
    </div>
  );
}

/**
 * @function EBillListItem
 * @param {STRING} amount - Allows us to set the amount
 * @param {STRING} date - Allows us to set the date
 * @return {React.Component}
 * @exported true
 * @description The EBillListItem shows one line in the eBill list. 
 * @example
 * <EBillListItem amount="[$X.XX]" date="[mmm dd]" />
 */

export function EBillListItem(props) {
  const controller = useGlobalController();
  const navigate = useRoute();
  const { close } = useBCMSModal();
  const bill = props.bill ?? Bill.firstInstanceOf();
  const amount = bill.amount.format();
  const date = bill.formatDate;
  const billDetailsPath = `/billerDetails/details/${ bill.biller.id }`;

  const markAsPaid = () => {
    bill.status = "Paid";
   
    BCMSRecord.batch([
      Bill.update(bill)
    ], controller);

    close("AlreadyPaidBillModal");
    navigate(billDetailsPath, { "state" : { "markedBillAsPaid" : true, "bill" : bill } });
  };

  const alreadyPaidBillModalData = { markAsPaid: markAsPaid };

  return(
      <div className="ebill-list-item flex-container flex-horizontal align-center space-between">
        <div className="flex-container flex-horizontal align-center">
          <b className="amount">{ amount }</b>
          <p>Due { date }</p>
        </div>
        <div className="right-links-container flex-container flex-horizontal align-center space-between">
          <div className="show-on-desktop">
            <Link className="semi-bold-text">
              <ModalOpen id="AlreadyPaidBillModal" data={ alreadyPaidBillModalData }>
                <span>Mark as Paid</span>
              </ModalOpen>
            </Link>
          </div>
          <span className="show-on-desktop divider"> | </span>
            <Link to={`/viewBillWorkflow/${ bill.id }`} className="semi-bold-text">
              <span>View</span>
            </Link>
        </div>
      </div>
  );
}